/**
 * Focuses on the first field that has an error and scrolls it into view.
 *
 * @param errors - Object containing field names as keys and error messages as values
 * @param sectionKey - Optional section key to limit search to a specific section
 */
export const focusErrorField = (
  errors: Record<string, string>,
  sectionKey?: string
): void => {
  if (!errors || Object.keys(errors).length === 0) {
    return;
  }

  const firstErrorField = Object.keys(errors).find(
    (field) => field !== '_form' && errors[field]
  );

  if (!firstErrorField) {
    return;
  }

  const sectionRoot = sectionKey
    ? document.querySelector(`[data-section="${sectionKey}"]`)
    : null;

  window.setTimeout(() => {
    const element = findFormFieldElement(
      firstErrorField,
      sectionRoot ?? document
    );

    if (!element) {
      return;
    }

    element.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });

    if (typeof element.focus === 'function') {
      element.focus({ preventScroll: true });
    }

    if (
      element instanceof HTMLInputElement &&
      ['text', 'email', 'number', 'tel', 'url'].includes(element.type)
    ) {
      element.select();
    }
  }, 150);
};

const escapeSelector = (value: string): string =>
  typeof CSS !== 'undefined' && 'escape' in CSS
    ? CSS.escape(value)
    : value.replace(/["\\]/g, '\\$&');

const resolveFocusableElement = (element: HTMLElement): HTMLElement | null => {
  if (element instanceof HTMLInputElement && element.type === 'hidden') {
    const button = element.parentElement?.querySelector<HTMLElement>('button');
    return button ?? element;
  }

  if (
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement ||
    element instanceof HTMLSelectElement ||
    element.tagName === 'BUTTON'
  ) {
    return element;
  }

  return (
    element.querySelector<HTMLElement>(
      "input:not([type='hidden']), textarea, select, button"
    ) ?? element
  );
};

const findFormFieldElement = (
  fieldName: string,
  root: ParentNode
): HTMLElement | null => {
  const escapedName = escapeSelector(fieldName);

  const selectors = [
    `[data-form-field="${escapedName}"]`,
    `[name="${escapedName}"]`,
    `#${escapedName}`,
    `[name^="${escapedName}-"]`,
    `[id^="${escapedName}-"]`,
    `textarea[name^="${escapedName}"]`,
  ];

  for (const selector of selectors) {
    const element = root.querySelector<HTMLElement>(selector);
    if (element) {
      return resolveFocusableElement(element);
    }
  }

  return null;
};
